import { TRPCClientError } from "@trpc/client"

// Statuses not worth retrying: 4xx fail identically on the same input, and hammering
// an unavailable/gateway-timed-out upstream amplifies the outage.
//
//   409 CONFLICT — a Convex `closed` refusal, i.e. a send into a CLOSED provider thread
//     (messages.sendThreadMessage). Retrying never reopens the thread, so it only delays
//     the error the member needs to see — and Healthie's createNote has no idempotency
//     key, so each extra attempt is a chance to post a duplicate message.
//   412 PRECONDITION_FAILED — the BFF holds no usable Healthie identity for the member, so
//     a provider-messaging request has no patient to act as. Either Patients says
//     there is no linked patient (a property of the session, identical on every retry), or
//     Healthie has just refused the id we had and the BFF dropped it. Not retried in that
//     second case either: Patients would hand back the same id and Healthie would
//     refuse it again, so a retry is a resolve-and-refuse loop across two upstreams.
//   429 TOO_MANY_REQUESTS — Healthie is throttling us (a Convex `rate_limited` refusal; see
//     convex/client.ts). The only entry here that is not merely pointless to retry but
//     actively harmful: a retry amplifies the exact condition the status reports, spending
//     the member's quota to be refused faster. Under polling that is self-sustaining, since
//     it fires hardest at the moment the upstream is least able to answer.
//
// 502 is deliberately ABSENT — a gateway fault is usually transient, so it IS retried.
const DO_NOT_RETRY_STATUSES = new Set<number>([400, 401, 403, 404, 409, 412, 429, 503, 504])

// tRPC-aware retry policy for React Query. The upstream status lives in
// `error.data.httpStatus` (not `error.statusCode`), so generic status-based policies
// miss it and retry everything; this one reads the right field.
export function trpcRetry(maxRetries: number) {
  return (failureCount: number, error: unknown): boolean => {
    if (failureCount >= maxRetries) return false

    // Client-side abort / torn socket — no upstream response to reason about.
    if (error instanceof Error && error.name === "AbortError") return false

    if (error instanceof TRPCClientError) {
      const status = (error.data as { httpStatus?: number } | undefined)?.httpStatus
      if (status != null && DO_NOT_RETRY_STATUSES.has(status)) return false
    }

    return true
  }
}
